import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import { useSearchParams } from "react-router-dom";
import { deleteAPIAsync, getAPI, getAPIAsync } from "../api/apiCalls";
import Provider from "../api/provider";
import { icon, inIframe, isNullOrEmpty, MAX_RECORD, WEBSITE_PATH, windowPostMessage } from "../common/common";
import { highlightSearchData } from "../common/commonJS";
import { resource } from "../common/resource";
import ListCards, { SetUserData } from "../components/cardsList";
import { CreateHeader } from "../components/headers";
import InboxSkeleton from "../components/skeletonLoaders/inboxSkeleton";
import { TextFieldOutlinedComponent } from "../components/textfield";
import { UserDetailsContext } from "../contexts/entityDetailsContext";
import { FollowingAccountsResponse, FollowingEntityInfo } from "../models/inbox";

const SearchUser = () => {
  const { userDetails, setUserDetails } = useContext<any>(UserDetailsContext);
  const [loader, setLoader] = useState(true);
  const [isAjax, setIsAjax] = useState<boolean>(true);
  const [pageNo, setPageNo] = useState<number>(0);
  const [totalFollowingCount, setTotalFollowingCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [recent, setRecent] = useState<FollowingEntityInfo[]>([]);
  const [recentMain, setRecentMain] = useState<FollowingEntityInfo[]>([]);
  const [following, setFollowing] = useState<FollowingEntityInfo[]>([]);
  const [displayViewAll, setDisplayViewAll] = useState<boolean>(true);
  const [delayDebounceFn, setDelayDebounceFn] = useState<any>();
  const [loaderPage, setLoaderPage] = useState(true);
  const [searchParam] = useSearchParams();
  const usercodenum: any = useRef();
  const apiCall: any = useRef(false);

  //#region useEffect
  useEffect(() => {
    if (!inIframe()) {
      window.location.href = WEBSITE_PATH;
      return;
    }
    usercodenum.current = encodeURIComponent(searchParam.get("userId"));
    window.addEventListener("message", handleWindowMessageReceived);

    return () => {
      window.removeEventListener("message", handleWindowMessageReceived);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNullOrEmpty(userDetails)) {
      windowPostMessage({ type: "getuserdetails" }, WEBSITE_PATH);
    } else {
      getUserRecentList();
      !apiCall.current && getUserFollowingList(searchText, pageNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  useEffect(() => {
    if (userDetails) window.history.replaceState("", "", `${window.location.origin}/search?userId=${userDetails.ucnum}&sessionId=${userDetails.sessionID}&query=${searchText}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    if (!isNullOrEmpty(userDetails)) {
      if (delayDebounceFn !== null) {
        clearTimeout(delayDebounceFn);
      }
      const debounceFn = setTimeout(async () => {
        !apiCall.current && (await getUserFollowingList(searchText, pageNo));
      }, 200);
      setDelayDebounceFn(debounceFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, pageNo]);

  //#endregion

  //#region APi calls

  const getUserFollowingList = async (searchTextVal: string, pageNoVal: number) => {
    apiCall.current = true;
    getAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.UserFollowing,
      {
        ucnum: userDetails.ucnum,
        isAllPostsCount: totalFollowingCount === 0,
        searchText: searchTextVal,
        pageSize: MAX_RECORD,
        pageNo: pageNoVal,
      },
      async (response: any) => {
        setLoaderPage(false);
        const data: FollowingAccountsResponse[] = response.data;
        let followingListCount: number = 0;
        if (data && data[0].followingEntityInfo) {
          if (data[0]?.totalFollowers) {
            setTotalFollowingCount(data[0]?.totalFollowers);
            followingListCount = data[0]?.totalFollowers;
          } else {
            followingListCount = totalFollowingCount;
          }

          if (data[0]?.followingEntityInfo) {
            const followingData = pageNoVal === 0 ? [] : following;
            const existingData = [...followingData, ...data[0].followingEntityInfo];
            if (searchTextVal !== "") {
              existingData.forEach((el) => {
                el.nameTemp = highlightSearchData(el.name, searchText);
              });
            } else {
              existingData.forEach((el) => {
                el.nameTemp = el.name;
              });
            }
            setFollowing(existingData);

            if (existingData.length >= followingListCount) {
              setLoaderAndAjax(false, false);
              return;
            }
          }
          setLoaderAndAjax(false, true);
        } else {
          setLoaderAndAjax(false, false);
          if (searchTextVal === "" && following.length === 0) await getUserRandomList();
        }
      },
      async (e: any) => {
        setLoaderPage(false);
        setLoaderAndAjax(false, false);
        isNullOrEmpty(e.data) ? windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH) : searchTextVal === "" && (await getUserRandomList());
      }
    );
  };

  const getUserRandomList = async () => {
    getAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.UserRandomSuggestion,
      {
        ucnum: userDetails.ucnum,
        pageSize: 1,
        pageNo: 0,
      },
      (response: any) => {
        const data: FollowingAccountsResponse[] = response.data;
        if (data && data[0]?.followingEntityInfo) {
          data[0]?.followingEntityInfo.forEach((el) => {
            el.nameTemp = el.name;
          });
          setFollowing(data[0]?.followingEntityInfo);
        }
      }
    );
  };

  const getUserRecentList = async () => {
    getAPI(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.RecentActivity,
      {
        ucnum: userDetails.ucnum,
        type: 1,
      },
      (response: any) => {
        const data: FollowingEntityInfo[] = response.data;
        if (data) {
          setRecent([...data.slice(0, 3)]);
          setRecentMain(data);
        }
      }
    );
  };

  const deleteAllUserRecentActivity = () => {
    deleteAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.DeletAllRecentActivity,
      {
        ucnum: userDetails.ucnum,
        type: 1,
      },
      () => {
        setRecent([]);
        setRecentMain([]);
      },
      () => {
        windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
      }
    );
  };
  //#endregion

  //#region Function calls
  const handleWindowMessageReceived = (event: any) => {
    if (event.origin && event.origin.includes(WEBSITE_PATH.slice(0, -1))) {
      const data = event.data;
      if (data.type === "userdetails") {
        SetUserData(usercodenum.current, data.userDetails, searchParam.get("sessionId"), setUserDetails);
      }
    }
  };

  const onScrollEvent = (e: any) => {
    const $eTarget = e.currentTarget;
    if (!isAjax || !($eTarget.scrollHeight - $eTarget.scrollTop - 10 <= $eTarget.clientHeight)) {
      return;
    }
    apiCall.current = false;
    setLoaderAndAjax(true, false);
    setPageNo(pageNo + MAX_RECORD);
  };

  const setLoaderAndAjax = (loader: boolean, ajax: boolean) => {
    setLoader(loader);
    setIsAjax(ajax);
  };

  const onTextChange = (e: any) => {
    setSearchParams(e.currentTarget.value);
  };

  const setSearchParams = (searchVal: string) => {
    setPageNo(0);
    setFollowing([]);
    apiCall.current = false;
    setSearchText(searchVal);
  };

  const onViewALlClick = () => {
    setRecent([...recentMain]);
    setDisplayViewAll(false);
  };

  const onRecentRemove = (data: any) => {
    const filtered = recentMain.filter((item: any) => {
      return item !== data;
    });
    setRecentMain(filtered);
    setRecent([...filtered.slice(0, 3)]);
  };

  //#endregion

  return (
    <Box className="inboxWindow flex-column pad-top-gutter">
      <CreateHeader title="Search contacts" isTextBox={false} />
      <Box className="searchBox pad-l-r-gutter">
        <TextFieldOutlinedComponent placeholder="Search" value={searchText} onChange={onTextChange} trailIcon={icon({ iconName: "search", iconColor: "icon-spanish-gray" })} />
      </Box>
      {loaderPage ? (
        <InboxSkeleton noTop={true} />
      ) : (
        <Box className="genericContentContainer pad-l-r-gutter mar-t-24" onScroll={onScrollEvent}>
          {recentMain.length > 0 && searchText === "" && (
            <Box className="flex-column max-width-100">
              <Box className="flex-row flex-align-center mar-b-8 ht-48" sx={{ paddingLeft: isMobileOnly ? "24px" : "28px" }}>
                <Typography variant="subtitle1" className="text-primary">
                  Recent
                </Typography>
              </Box>
              <ListCards data={recent} type="followingrecent" actionCallback={onRecentRemove} />
              {recentMain.length > 3 && (
                <Box className="flex-center" sx={{ paddingTop: "16px" }}>
                  {displayViewAll ? (
                    <Button variant="text" size="large" onClick={onViewALlClick}>
                      View all
                    </Button>
                  ) : (
                    <Button variant="text" size="large" onClick={deleteAllUserRecentActivity}>
                      Clear all
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}

          {following.length > 0 && (
            <Box className={`flex-column w-per-100 ${recentMain.length > 0 && searchText === "" ? "mar-t-24" : ""}`}>
              <Box className="flex-row flex-align-center mar-b-8 ht-48" sx={{ paddingLeft: isMobileOnly ? "24px" : "28px" }}>
                <Typography variant="subtitle1" className="text-primary">
                  Suggested
                </Typography>
              </Box>
              <ListCards data={following} type="followingsearch" />
              {loader && (
                <Box className="flex-center ht-64" sx={{ m: 2 }}>
                  <CircularProgress className="box-24" />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchUser;
